// src/components/TradingChart.jsx
import React, { useEffect, useRef } from 'react';
import { createChart, ColorType, AreaSeries } from 'lightweight-charts';

const TradingChart = ({ currentPrice, openPrice, priceHistory = [], selectedCrypto }) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const targetLineRef = useRef(null);

  // ۱. ساخت و مقداردهی اولیه چارت
  useEffect(() => {
    if (!chartContainerRef.current) return;

    // تنظیمات ظاهر چارت مدرن و نئونی
    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: '#0B0E17' },
        textColor: '#9CA3AF',
      },
      grid: {
        vertLines: { color: 'rgba(255, 255, 255, 0.03)' },
        horzLines: { color: 'rgba(255, 255, 255, 0.03)' },
      },
      width: chartContainerRef.current.clientWidth,
      height: 280,
      timeScale: {
        timeVisible: true,
        secondsVisible: true,
        borderColor: 'rgba(255, 255, 255, 0.05)',
      },
      rightPriceScale: {
        borderColor: 'rgba(255, 255, 255, 0.05)',
      },
      crosshair: {
        vertLine: { color: '#00E676', width: 1, style: 3 },
        horzLine: { color: '#00E676', width: 1, style: 3 },
      },
    });

    const areaSeries = chart.addSeries(AreaSeries, {
      topColor: 'rgba(0, 230, 118, 0.4)',
      bottomColor: 'rgba(0, 230, 118, 0.0)',
      lineColor: '#00E676',
      lineWidth: 2,
    });

    chartRef.current = chart;
    seriesRef.current = areaSeries;

    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, []);

  // ۲. لود مجدد هیستوری هنگام تغییر ارز یا ورود به تب (حل باگ ۱ و باگ ۲)
  useEffect(() => {
    if (!seriesRef.current || !priceHistory) return;

    // تبدیل و مرتب‌سازی دیتاها برای جلوگیری از خطای duplicate/unordered time در lightweight-charts
    const formattedData = priceHistory
      .map((item) => ({
        time: Math.floor((item.time || Date.now()) / 1000),
        value: parseFloat(item.price),
      }))
      .filter((item, index, self) => index === 0 || item.time > self[index - 1].time);

    if (formattedData.length > 0) {
      seriesRef.current.setData(formattedData);
      chartRef.current?.timeScale().fitContent();
    } else {
      seriesRef.current.setData([]);
    }
  }, [selectedCrypto, priceHistory]);

  // ۳. به‌روزرسانی قیمت لایو و تغییر رنگ بر اساس openPrice
  useEffect(() => {
    if (seriesRef.current && currentPrice && !isNaN(parseFloat(currentPrice))) {
      const now = Math.floor(Date.now() / 1000);

      try {
        seriesRef.current.update({
          time: now,
          value: parseFloat(currentPrice),
        });
      } catch (err) {
        // در صورت برابری timestamp فقط مقدار بروزرسانی می‌شود
      }

      // تغییر رنگ چارت متناسب با صعودی/نزولی بودن نسبت به قیمت Open (Target)
      if (openPrice && !isNaN(parseFloat(openPrice))) {
        const isUp = parseFloat(currentPrice) >= parseFloat(openPrice);
        seriesRef.current.applyOptions({
          topColor: isUp ? 'rgba(0, 230, 118, 0.35)' : 'rgba(255, 61, 87, 0.35)',
          lineColor: isUp ? '#00E676' : '#FF3D57',
        });
      }
    }
  }, [currentPrice, openPrice]);

  // ۴. رسم خط Target/Open Price روی چارت
  useEffect(() => {
    if (!seriesRef.current) return;

    // پاک کردن خط قبلی در صورت وجود
    if (targetLineRef.current) {
      seriesRef.current.removePriceLine(targetLineRef.current);
      targetLineRef.current = null;
    }

    if (openPrice && !isNaN(parseFloat(openPrice))) {
      targetLineRef.current = seriesRef.current.createPriceLine({
        price: parseFloat(openPrice),
        color: '#A855F7',
        lineWidth: 1,
        lineStyle: 2, // Dashed line
        axisLabelVisible: true,
        title: 'Target',
      });
    }
  }, [openPrice]);

  return (
    <div className="relative w-full overflow-hidden rounded-2xl bg-cardDark/80 p-2 border border-white/5 backdrop-blur-xl shadow-2xl">
      <div ref={chartContainerRef} className="w-full h-[280px]" />
    </div>
  );
};

export default TradingChart;